<template>
  <div class="min-h-screen flex flex-col bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">
    <Header />
    <main :class="['flex-1 relative', isNative ? 'safe-bottom' : '']">
      <router-view v-slot="{ Component }">
        <Transition name="page" mode="out-in">
          <component :is="Component" />
        </Transition>
      </router-view>
    </main>
    <Footer :class="isNative ? 'safe-bottom' : ''" />
    <ToastContainer />
    <SplashScreen v-if="splashVisible" />
    <OnboardingTour v-if="tourSteps" :steps="tourSteps" @done="onTourDone" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import Header from './components/layout/Header.vue'
import Footer from './components/layout/Footer.vue'
import ToastContainer from './components/common/ToastContainer.vue'
import SplashScreen from './components/common/SplashScreen.vue'
import OnboardingTour from './components/common/OnboardingTour.vue'
import { usePlatform } from './composables/usePlatform'
import { useAuthStore } from './stores/auth'
import { getTourState, completeTour } from './api/me'
import { coachTour, playerTour, type TourStep } from './tours'

const { isNative } = usePlatform()
const authStore = useAuthStore()
const route = useRoute()

// --- First-login onboarding walkthrough (coach or player flavored) ---
const tourSteps = ref<TourStep[] | null>(null)
const PUBLIC_PATHS = ['/', '/demo', '/login', '/privacy-policy', '/terms-of-service']

async function maybeStartTour() {
  if (!authStore.isAuthenticated) return
  if (PUBLIC_PATHS.includes(route.path) || route.path.startsWith('/invite/') || route.path.startsWith('/auth/')) return
  try {
    const state = await getTourState()
    if (state.completed) return
    const steps = authStore.isCoach || authStore.isAdmin ? coachTour : playerTour
    // Team-page steps need the active team's URL; drop the route when the
    // user has no active team yet (the card still shows, centered).
    const teamRoute = authStore.activeTeamId ? `/teams/${authStore.activeTeamId}` : undefined
    tourSteps.value = steps.map(step =>
      step.route === '__team__' ? { ...step, route: teamRoute } : step
    )
  } catch {
    // Tour is a nicety — never block the app on it.
  }
}

async function onTourDone() {
  tourSteps.value = null
  try {
    await completeTour()
  } catch { /* non-fatal */ }
}

// Splash screen — shown for 2.5 s on app open when a session exists.
const SPLASH_DURATION_MS = 2500
const SPLASH_BG = '/splash-bg.jpg'
const splashVisible = ref(false)
let splashTimer: ReturnType<typeof setTimeout> | null = null

function showSplash() {
  splashVisible.value = true
  if (splashTimer !== null) clearTimeout(splashTimer)
  splashTimer = setTimeout(() => {
    splashVisible.value = false
    splashTimer = null
  }, SPLASH_DURATION_MS)
}

// Preload the background image, then reveal the splash so users never
// see the overlay appear with a blank / partially-loaded image behind it.
// Falls back to showing immediately after 400 ms if the network is slow —
// the navy background already looks intentional at that point.
function fireSplash() {
  const img = new Image()
  img.onload = showSplash
  img.onerror = showSplash   // show even if image fails — still a nice navy overlay
  img.src = SPLASH_BG
  // Fallback: if no load/error fires within 400 ms the image is likely
  // already cached (complete) and the events fired synchronously above,
  // or the browser is very slow — either way show the splash.
  setTimeout(() => {
    if (!splashVisible.value) showSplash()
  }, 400)
}

// Shown once per browser session: sessionStorage survives refreshes in the
// same tab but resets on a fresh tab/window, so reloads skip the splash.
const SPLASH_SEEN_KEY = 'splash_seen'

onMounted(() => {
  // Fire on cold-start for returning users (token already in storage).
  if (
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('token') &&
    !sessionStorage.getItem(SPLASH_SEEN_KEY)
  ) {
    sessionStorage.setItem(SPLASH_SEEN_KEY, '1')
    fireSplash()
  }
  // Give the splash + initial data a beat, then offer the walkthrough.
  setTimeout(maybeStartTour, 3200)
})

onUnmounted(() => {
  if (splashTimer !== null) clearTimeout(splashTimer)
})
</script>

<style>
.page-enter-active {
  animation: page-enter 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.page-leave-active {
  animation: page-leave 0.2s ease-in forwards;
}

@keyframes page-enter {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes page-leave {
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(-6px); }
}
</style>
